import { useCallback } from "react";
import { useSelector } from "react-redux";
import { MultiPoint } from "@evergis/sgis/es/features/MultiPoint";
import { PointFeature } from "@evergis/sgis/es/features/PointFeature";
import { Polygon } from "@evergis/sgis/es/features/Polygon";
import { Polyline } from "@evergis/sgis/es/features/Polyline";
import { GeometryType } from "@evergis/api";
import { getCrs } from "@evergis/react";

import { SelectedPointSymbol } from "../../../react-sgis/symbols/selectedSymbols/SelectedPointSymbol";
import { SelectedPolySymbol } from "../../../react-sgis/symbols/selectedSymbols/SelectedPolySymbol";

import { getAppConfig } from "../../../ducks/appConfig";

import {
  DEFAULT_SELECTED_POINT_OPTIONS,
  DEFAULT_SELECTED_POLYGON_OPTIONS,
  DEFAULT_SELECTED_POLYLINE_OPTIONS,
  selectedPoint,
  selectedPolygon,
  selectedPolyline,
} from "../../../react-sgis/symbols/selectedSymbols/selectedSymbols";

import { FeatureDcExtended } from "../../../ducks/selectFeatures/types";

const featureConstructorMap = {
  point: PointFeature,
  multipoint: MultiPoint,
  polyline: Polyline,
  multipolygon: Polygon,
} as const;

type FeatureInstance = InstanceType<ValueOf<typeof featureConstructorMap>> & {
  isSelected: boolean;
};

const symbolMapOptions = {
  point: DEFAULT_SELECTED_POINT_OPTIONS,
  multipoint: DEFAULT_SELECTED_POINT_OPTIONS,
  polyline: DEFAULT_SELECTED_POLYLINE_OPTIONS,
  multipolygon: DEFAULT_SELECTED_POLYGON_OPTIONS,
} as Record<GeometryType, unknown>;

const symbolMapType = {
  point: SelectedPointSymbol,
  multipoint: SelectedPointSymbol,
  polyline: SelectedPolySymbol,
  multipolygon: SelectedPolySymbol,
} as Record<GeometryType, typeof SelectedPointSymbol | typeof SelectedPolySymbol>;

const symbolMap = {
  point: selectedPoint,
  multipoint: selectedPoint,
  polyline: selectedPolyline,
  multipolygon: selectedPolygon,
} as Record<GeometryType, unknown>;

type AvailableFeatureTypes = keyof typeof symbolMap;

const checkFeatureType = (type: GeometryType): type is AvailableFeatureTypes => Object.keys(symbolMap).includes(type);

export const createFeature = (
  { geometry, id }: Partial<FeatureDcExtended>,
  symbols: Record<GeometryType, unknown>,
  selectedId?: string,
): FeatureInstance | null => {
  if (!geometry) {
    return null;
  }

  const { type, coordinates, sr } = geometry;

  if (!checkFeatureType(type)) {
    return null;
  }

  const featureCrs = getCrs(sr);
  const FeatureConstructor = featureConstructorMap[type];
  const symbol = symbols[type];

  if (!FeatureConstructor || !featureCrs || !symbol) {
    return null;
  }

  const feature = new FeatureConstructor(coordinates, { symbol, crs: featureCrs }) as FeatureInstance;

  feature.isSelected = !!selectedId && id === selectedId;

  return feature;
};

export const useCreateFeature = () => {
  const { settings } = useSelector(getAppConfig);
  const selectSymbolOptions = settings?.symbols?.selectSymbolParameters;
  const symbols = symbolMap;

  if (selectSymbolOptions) {
    Object.entries(symbolMapType).forEach(([type, SymbolConstructor]) => {
      symbols[type as GeometryType] = new SymbolConstructor({
        ...symbolMapOptions[type],
        ...selectSymbolOptions,
        selectedFillColor: selectSymbolOptions?.fillColor || symbolMapOptions[type].fillColor,
        selectedStrokeColor: selectSymbolOptions?.strokeColor || symbolMapOptions[type].strokeColor,
        selectedStrokeWidth: selectSymbolOptions?.strokeWidth || symbolMapOptions[type].strokeWidth,
      });
    });
  }

  return useCallback(
    (geometryParams: Partial<FeatureDcExtended>, selectedId?: string) =>
      createFeature(geometryParams, symbols, selectedId),
    [selectSymbolOptions],
  );
};
